/**
 * Où se pose une liste ancrée sous son champ (`<Overlay placement="anchor">`) : sous le champ, à sa largeur, et
 * au-dessus quand la place manque en bas. Pur : le champ et le calque arrivent mesurés, dans le même repère (celui
 * du calque).
 */

/** Un rectangle mesuré : le champ, dans le repère du calque. */
export type AnchorRect = { x: number; y: number; width: number; height: number }

/** Le cadre de la liste : `top` quand elle descend sous le champ, `bottom` quand elle monte au-dessus. */
export type AnchoredFrame = { left: number; width: number; maxHeight: number } & (
    { top: number; bottom?: undefined } | { bottom: number; top?: undefined }
)

/** L'écart entre le champ et sa liste, et la marge qu'elle garde avec les bords. */
const GAP = 4
const MARGIN = 8
/** La hauteur au-delà de laquelle une liste défile. */
export const ANCHORED_MAX_HEIGHT = 280
/** En dessous de cette place, une liste serait trop courte pour qu'on y choisisse : elle monte, si c'est mieux. */
const MIN_COMFORT = 160

export function anchoredFrame(input: {
    anchor: AnchorRect
    host: { width: number; height: number }
    /** Le haut du clavier, quand il est ouvert : la liste ne descend pas dessous. */
    keyboardTop?: number | null
    maxHeight?: number
}): AnchoredFrame {
    const { anchor, host, keyboardTop, maxHeight = ANCHORED_MAX_HEIGHT } = input
    const floor = keyboardTop != null ? Math.min(keyboardTop, host.height) : host.height
    const below = floor - (anchor.y + anchor.height + GAP) - MARGIN
    const above = anchor.y - GAP - MARGIN

    const width = Math.min(anchor.width, Math.max(0, host.width - 2 * MARGIN))
    const left = Math.min(Math.max(anchor.x, MARGIN), Math.max(MARGIN, host.width - width - MARGIN))

    if (below < Math.min(maxHeight, MIN_COMFORT) && above > below) {
        return { left, width, bottom: host.height - (anchor.y - GAP), maxHeight: Math.min(maxHeight, above) }
    }
    return { left, width, top: anchor.y + anchor.height + GAP, maxHeight: Math.max(0, Math.min(maxHeight, below)) }
}
